import React, { useState } from 'react';
import {connect, DispatchProp} from 'react-redux';
import {useFormik} from 'formik';
import classNames from 'classnames';
import * as Yup from 'yup';
import {
  CCol,
  CForm,
  CFormGroup,
  CRow,
  CInput,
  CAlert,
  CLabel,
  CSelect, 
  CInputCheckbox, 
  CInputGroup, 
  CInputGroupText, 
  CInputGroupAppend,
  CTooltip,
  CImg
} from '@coreui/react';

import {some, toPairs, get} from 'lodash';
import {useHistory} from 'react-router-dom';
import CIcon from '@coreui/icons-react';
import {CountryCode, RegisterInfo} from '../../shared/models/register';
import LoadingButton from '../../shared/components/LoadingButton';
import {DEFAULT_REFERRER} from '../../shared/referrers';
import eye from '../../assets/images/eye.svg';
import eyeStricken from '../../assets/images/eye-stricken.svg';
import tooltip from '../../assets/images/tool_tip.svg';
import { InternalSignUpRequest } from '../actions/internalSignUp.action';
import { RootState } from '../../store/state';

const TECHNICIAN_DEFAULT_OPTION = '1-5';

const SHARD_OPTIONS = [
  {
    value: CountryCode.US,
    label: 'United States / Canada',
    tooltip: 'Includes all U.S. states, territories, and Canada.',
  },
  {
    value: CountryCode.EU,
    label: 'European Union (EU)',
    tooltip: 'Includes all current EU member countries.'
  },
  {
    value: CountryCode.restOfWorld,
    label: 'Rest of World (Non-US/Canada/EU)',
  },
];


const TECHNICIAN_OPTIONS: { value: string; label: string }[] = [
  {value: TECHNICIAN_DEFAULT_OPTION, label: '1-5'},
  {value: '6-10', label: '6-10'},
  {value: '11-50', label: '11-50'},
  {value: '50+', label: '50+'},
];

const validationSchema = Yup.object({
  name: Yup.string().required('Company name is required'),
  agent: Yup.object({
    firstName: Yup.string().required('First Name is required'),
    lastName: Yup.string().required('Last Name is required'),
    email: Yup.string().email('Email must be valid').required('Email is required'),
    phone: Yup.string(),
    password: Yup.string().matches(/^(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/, 'Must contain 8 characters, at least 1 uppercase, 1 number').required("This field is required"),
    confirm_password: Yup.string()
      .required('Confirm password is required')
      .oneOf([Yup.ref('password'), null], 'Passwords must match'),
  }),
  info: Yup.object({
    num_employees: Yup.string(),
    industry: Yup.string(),
    job_title: Yup.string(),
  }),
  termsOfUse: Yup.boolean().oneOf([true], 'Accept Terms & Conditions is required'),
  shard: Yup.string().oneOf([CountryCode.US, CountryCode.EU, CountryCode.restOfWorld]).required('Country is required'),
});

const initialValues: RegistrationFormFields = {
  name: '',
  agent: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
  },
  info: {
    num_employees: TECHNICIAN_DEFAULT_OPTION,
    industry: '',
    job_title: '',
    referrer: '',
  },
  termsOfUse: false,
  shard: '',
};

interface InternalSignUpProps extends DispatchProp {
  isLoading: boolean;
  success: boolean;
  errorText: string;
}

export interface RegistrationFormFields {
  name: string;
  agent: AgentFormFields;
  info: RegisterInfo;
  termsOfUse?: boolean;
  shard: string;
}

export interface AgentFormFields {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirm_password?: string;
}
const InternalSignUp = (props: InternalSignUpProps) => {
  const { dispatch, isLoading, success, errorText } = props;
  const history = useHistory();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = (values: RegistrationFormFields) => {
    const { agent } = values;

    dispatch(new InternalSignUpRequest({
      orgName: values.name,
      adminFirstName: agent.firstName,
      adminLastName: agent.lastName,
      adminEmail: agent.email,
      adminPhone: agent.phone,
      password: agent.password,
      info: values.info,
      shard: values.shard,
    }));
  };

  initialValues.info.referrer = DEFAULT_REFERRER;

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  React.useEffect(() => {
    if (success) {
      history.push('/dashboard');
    }
  }, [success, history]);

  React.useEffect(() => {
    if (errorText) {
      toPairs(initialValues).forEach(([field]) => {
        formik.setFieldTouched(field, false);
      });
    }
  }, [errorText]);

  const isInvalid = (fieldName: string) => get(formik.touched, fieldName) && get(formik.errors, fieldName);

  const isTouched = () => some(formik.touched, Boolean);


  return (
    <>
      <CForm onSubmit={formik.handleSubmit}>
        <CInput id="info.referrer" type="hidden" name="info.referrer" {...formik.getFieldProps('info.referrer')} />
        <h1 className="text-center">Internal Registration Only – Not for Public Use</h1>
        <p className="text-center text-danger">
            <strong>WARNING:</strong>
            This page is for authorized Glympse users to create PRO organizations with custom or ACH billing.
            <br />
            This flow does not use standard self-serve registration or credit card billing.
        </p>

        <CRow>
          <CCol lg="6">
            <CFormGroup>
              <CLabel htmlFor="agent.firstName" className="required">Name</CLabel>
              <CInput
                id="agent.firstName"
                type="text"
                placeholder="First"
                name="agent.firstName"
                className={classNames({'is-invalid': isInvalid('agent.firstName')})}
                {...formik.getFieldProps('agent.firstName')}
              />
              {isInvalid('agent.firstName') && <div className="invalid-feedback">{formik.errors.agent.firstName}</div>}
            </CFormGroup>
          </CCol>
          <CCol lg="6">
            <CFormGroup>
              <CLabel htmlFor="agent.lastName">&nbsp;</CLabel>
              <CInput
                id="agent.lastName"
                type="text"
                placeholder="Last"
                name="agent.lastName"
                className={classNames({'is-invalid': isInvalid('agent.lastName')})}
                {...formik.getFieldProps('agent.lastName')}
              />
              {isInvalid('agent.lastName') && <div className="invalid-feedback">{formik.errors.agent.lastName}</div>}
            </CFormGroup>
          </CCol>
        </CRow>
        <CRow>
          <CCol lg="6">
            <CFormGroup>
              <CLabel htmlFor="name" className="required">Company Name</CLabel>
              <CInput
                id="name"
                type="text"
                name="name"
                className={classNames({'is-invalid': isInvalid('name')})}
                {...formik.getFieldProps('name')}
              />
              {isInvalid('name') && <div className="invalid-feedback">{formik.errors.name}</div>}
            </CFormGroup>
          </CCol>
          <CCol lg="6">
            <CFormGroup>
              <CLabel htmlFor="info.job_title">Job Title</CLabel>
              <CInput
                id="info.job_title"
                type="text"
                name="info.job_title"
                className={classNames({'is-invalid': isInvalid('info.job_title')})}
                {...formik.getFieldProps('info.job_title')}
              />
              {isInvalid('info.job_title') && <div className="invalid-feedback">{formik.errors.info.job_title}</div>}
            </CFormGroup>
          </CCol>
        </CRow>

        <CRow>
          <CCol lg="6">
            <CFormGroup>
              <CLabel htmlFor="industry">Industry</CLabel>
              <CInput
                id="industry"
                type="text"
                name="info.industry"
                className={classNames({'is-invalid': isInvalid('info.industry')})}
                {...formik.getFieldProps('info.industry')}
              />
              {isInvalid('info.industry') && <div className="invalid-feedback">{formik.errors.info.industry}</div>}
            </CFormGroup>
          </CCol>
          <CCol lg="6">
            <CFormGroup>
              <CLabel htmlFor="num_employees">Number of Technicians</CLabel>
              <CSelect
                id="num_employees"
                name="info.num_employees"
                className={classNames({'is-invalid': isInvalid('info.num_employees')})}
                {...formik.getFieldProps('info.num_employees')}
              >
                {TECHNICIAN_OPTIONS.map(({value, label}) => <option key={value} value={value}>{label}</option>)}
              </CSelect>
              {isInvalid('info.num_employees') &&
                <div className="invalid-feedback">{formik.errors.info.num_employees}</div>}
            </CFormGroup>
          </CCol>
        </CRow>
        <CRow>
          <CCol lg="6">
            <CFormGroup>
              <CLabel htmlFor="agent.email" className="required">Email</CLabel>
              <CInput
                id="agent.email"
                type="text"
                name="agent.email"
                className={classNames({'is-invalid': isInvalid('agent.email')})}
                {...formik.getFieldProps('agent.email')}
              />
              {isInvalid('agent.email') && <div className="invalid-feedback">{formik.errors.agent.email}</div>}
            </CFormGroup>
          </CCol>
          <CCol lg="6">
            <CFormGroup>
              <CLabel htmlFor="agent.phone">Phone</CLabel>
              <CInput
                id="agent.phone"
                type="text"
                name="agent.phone"
                className={classNames({'is-invalid': isInvalid('agent.phone')})}
                {...formik.getFieldProps('agent.phone')}
              />
              {isInvalid('agent.phone') && <div className="invalid-feedback">{formik.errors.agent.phone}</div>}
            </CFormGroup>
          </CCol>
        </CRow>

        <CRow>
          <CCol lg="6">
            <CFormGroup>
              <CLabel htmlFor="agent.password" className="required">Password</CLabel>
              <CInputGroup>
                <CInput
                  id="agent.password"
                  type={showPassword ? 'text' : 'password'}
                  name="agent.password"
                  className={classNames({'is-invalid': isInvalid('agent.password')})}
                  {...formik.getFieldProps('agent.password')}
                />
                <CInputGroupAppend>
                  <CInputGroupText onClick={() => setShowPassword(!showPassword)} className="cursor-pointer">
                    <CIcon src={showPassword ? eye : eyeStricken} className="password-eye"/>
                  </CInputGroupText>
                </CInputGroupAppend>
                {isInvalid('agent.password') && <div className="invalid-feedback">{formik.errors.agent.password}</div>}
              </CInputGroup>
            </CFormGroup>
          </CCol>
          <CCol lg="6">
            <CFormGroup>
              <CLabel htmlFor="agent.confirm_password" className="required">Confirm Password</CLabel>
              <CInputGroup>
                <CInput
                  id="agent.confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="agent.confirm_password"
                  className={classNames({'is-invalid': isInvalid('agent.confirm_password')})}
                  {...formik.getFieldProps('agent.confirm_password')}
                />
                <CInputGroupAppend>
                  <CInputGroupText onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="cursor-pointer">
                    <CIcon src={showConfirmPassword ? eye : eyeStricken} className="password-eye"/>
                  </CInputGroupText>
                </CInputGroupAppend>
                {isInvalid('agent.confirm_password') &&
                <div className="invalid-feedback">{formik.errors.agent.confirm_password}</div>}
              </CInputGroup>
            </CFormGroup>
          </CCol>
        </CRow>

        <CRow>
          <CCol lg="6">
            <CFormGroup>
              <CLabel htmlFor="shard" className="required">
                Country
                <CTooltip
                  content={
                    <div>
                      <b>US/Canada:</b> All U.S. states, territories, and Canada. 
                      <b>EU:</b> All current EU members.
                      <b>Rest of World:</b> All other countries not in US/Canada or EU (e.g., Mexico, Latin America, Asia, Africa, Australia)
                    </div>
                  }
                  placement="bottom"
                >
                  <CImg src={tooltip} className="ml-2 mb-2 glympser-toolTip"/>
                </CTooltip>
              </CLabel>
              <CSelect
                id="shard"
                name="shard"
                className={classNames({'is-invalid': isInvalid('shard')})}
                {...formik.getFieldProps('shard')}
              >
                <option value="" disabled hidden>Select Country</option>
                {SHARD_OPTIONS.map(({ value, label }) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </CSelect>
              {isInvalid('shard') && <div className="invalid-feedback">{formik.errors.shard}</div>}
            </CFormGroup>
          </CCol>
        </CRow>

        <CRow>
          <CCol>
            <CFormGroup variant="checkbox" className="checkbox mb-2">
              <CInputCheckbox
                id="termsOfUse"
                name="termsOfUse"
                className={classNames({'is-invalid': isInvalid('termsOfUse')})}
                {...formik.getFieldProps({
                  name: 'termsOfUse',
                  type: 'checkbox'
                })}
              />
              <CLabel variant="checkbox" htmlFor="termsOfUse" className="text-black">I agree to the Glympse <a
                className="link" href="https://glympse.com/terms/" target="_blank" rel="noreferrer">terms of
                use.</a></CLabel>
              {isInvalid('termsOfUse') && <div className="invalid-feedback">{formik.errors.termsOfUse}</div>}
            </CFormGroup>
          </CCol>
        </CRow>

        <CRow>
          <CCol xs="9" md="6">
            <LoadingButton
              type="submit"
              className="bg-color-orange border-0"
              isLoading={isLoading}
            >
              Register
            </LoadingButton>
          </CCol>
        </CRow>
      </CForm>
      {
        (errorText && !isTouched()) ? (
          <CAlert className="my-2" color="warning" fade>{errorText}</CAlert>
        ) : null
      }
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  isLoading: state?.internalSignUp?.isLoading || false,
  success: state?.internalSignUp?.success || false,
  errorText: state?.internalSignUp?.errorText || '',
});

export default connect(mapStateToProps)(InternalSignUp);
